import {
  useEditSubTrayItemMutation,
  useGetSubTrayItemById,
} from 'lib/apollo/hooks'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import useTrayMap from 'views/SPDLayout/SPD/TrayMap/TrayMap.logic'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'
import { updateTrayImageHistory } from 'lib/utils/updateTrayImageHistory'
import { Typography } from '@mui/material'
import { QrCode2 } from '@mui/icons-material'
import { enqueueSnackbar } from 'notistack'
import {
  SubTrayItemProductDetails,
  TrayImageRecord,
} from 'lib/services/api/product-service/receiveItems/types'
import { useSPDScan } from 'views/SPDLayout/SPD/Scan/Scan.context'
import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import useGrayScaleLogic from 'components/organisms/GrayScale/GrayScale.logic'
import { getLatestTrayImage } from 'lib/utils/getLatestTrayImage'
import dayjs from 'lib/dayjs'
import { useAssignedDigitalTrays } from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays.context'
import useDTMS3Image from 'lib/utils/useDTMS3Image'
import useSearchProduct from 'lib/services/api/product-service/searchProduct'
import { getScrewSizeFromProduct } from 'lib/utils/csvProductMapping'

const useEditTray = () => {
  const navigate = useNavigate()
  const { trayId, subTrayItemId } = useParams()
  const { pathname } = useLocation()
  useFlags()

  const {
    trayScrews,
    isLoadingWithUDIs,
    setIsLoadingWithUDIs,
    isAdditionalDeviceCountModalOpen,
    trayPlates,
  } = useTrayMap()
  const { isLoadMultipleBoxVisible } = useSPDScan()
  const {
    setParLevel,
    otherItems,
    setOtherItems,
    customImplants,
    setCustomImplants,
    setTrayScrews: setSpdTrayScrews,
    setTrayPlates: setSpdTrayPlates,
  } = useSPD()
  const { loading, error, subTrayItem, getSubTrayItem, refetch } =
    useGetSubTrayItemById()
  const { editSubTrayItem } = useEditSubTrayItemMutation()
  const { setSelectedSubTray } = useAssignedDigitalTrays()

  const [isInEditMode, setIsInEditMode] = useState<boolean>(false)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false)
  const [isRejecting, setIsRejecting] = useState<boolean>(false)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [trayDetails, setTrayDetails] = useState<
    SubTrayItemProductDetails | undefined
  >(undefined)
  const [imageScale, setImageScale] = useState<number>(1)
  const [imageRotation, setImageRotation] = useState<number>(0)
  const [isLegendOpen, setIsLegendOpen] = useState<boolean>(false)
  const [isCustomImplantModalOpen, setIsCustomImplantModalOpen] =
    useState<boolean>(false)
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0)
  const [imageHistory, setImageHistory] = useState<TrayImageRecord[]>([])
  const [isViewingHistory, setIsViewingHistory] = useState<boolean>(false)
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState<boolean>(false)

  const [
    isReferenceImageAccurateModalOpen,
    setIsReferenceImageAccurateModalOpen,
  ] = useState<boolean>(false)
  const [isUDIScannerModalOpen, setIsUDIScannerModalOpen] =
    useState<boolean>(false)
  const [scannedUDI, setScannedUDI] = useState<string>('')

  const [initialTrayScrews, setInitialTrayScrews] = useState<any[]>([])

  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
  const newlyLoadedScrews = trayScrews.filter(
    (screw) =>
      screw.loadedViaUDI &&
      screw.loadedAt &&
      new Date(screw.loadedAt) >= oneDayAgo
  )
  const [isQuantityExceededModalOpen, setIsQuantityExceededModalOpen] =
    useState<boolean>(false)
  const [quantityValidationData, setQuantityValidationData] = useState<{
    scannedQuantity: number
    detectedQuantity: number
    productName: string
  } | null>(null)

  const {
    screwAnnotations,
    plateAnnotations,
    drillAnnotations,
    loadAllScrews,
    increaseAllPlatesBy,
    isAllScrewsLoaded,
    isAllPlatesLoaded,
  } = useGrayScaleLogic({
    trayType: trayDetails?.trayType,
  })

  const hasPlates = plateAnnotations.length > 0 || drillAnnotations.length > 0
  const hasScrews = screwAnnotations.length > 0
  const isPlatesOnly = hasPlates && !hasScrews
  const plates = trayDetails?.plates || []
  const totalPlateCount = plates.reduce(
    (acc, plate) => acc + plate.plateCount,
    0
  )
  const existingPlates = plates.filter((plate) => plate.plateCount > 0) || []
  const totalScrews = trayDetails?.screws.length

  const isInstrumentTray = trayDetails?.trayType === 'instrument'
  const isCustomImplantTray = trayDetails?.trayType === 'custom implants'
  const hasMultipleImages = imageHistory.length > 1
  const currentImage = imageHistory[currentImageIndex]
  const formattedTimestamp = currentImage?.timestamp
    ? dayjs(currentImage.timestamp).format('MMM D, YYYY h:mm A')
    : ''
  const rawTrayImage =
    currentImage?.imageUrl || getLatestTrayImage(trayDetails, ['rep', 'spd'])

  const { imageUrl: trayImage, isLoading: isImageLoading } = useDTMS3Image({
    url: rawTrayImage,
  })

  const getReferenceImageTitle = () => {
    if (!currentImage) return 'Reference Image'

    if (currentImage.addedBy.role === 'nurse') {
      return 'End of Case Reference Image'
    } else if (currentImage.addedBy.role === 'rep') {
      return 'Reference Image (Captured in Vendor Distribution Center)'
    } else if (currentImage.addedBy.email === 'UDI Scanner') {
      return 'Reference Image (Captured during UDI Scanning)'
    } else {
      return 'Reference Image (Captured in Vendor Distribution Center)'
    }
  }
  const referenceImageTitle = getReferenceImageTitle()

  const goToPreviousImage = () => {
    if (currentImageIndex < imageHistory.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1)
      setIsViewingHistory(true)
    }
  }

  const goToNextImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1)
      setIsViewingHistory(currentImageIndex - 1 !== 0)
    }
  }

  const goToLatestImage = () => {
    setCurrentImageIndex(0)
    setIsViewingHistory(false)
  }

  // Allow UDI workflow for plate-only trays as well
  useEffect(() => {
    return
  }, [])

  useEffect(() => {
    getSubTrayItem({ variables: { subTrayItemId: Number(subTrayItemId) } })
    refetch()
  }, [subTrayItemId, pathname, getSubTrayItem, refetch])

  useEffect(() => {
    if (subTrayItem) {
      setTrayDetails(extractSubTrayJSONData(subTrayItem))
      setSelectedSubTray(subTrayItem)
    }
  }, [subTrayItem, setSelectedSubTray])

  useEffect(() => {
    if (subTrayItemId && !loading && !error && subTrayItem && trayDetails) {
      setSpdTrayScrews(trayDetails.screws || [])
      setSpdTrayPlates(trayDetails.plates || [])
      setOtherItems(trayDetails?.otherItems || [])
      setCustomImplants(trayDetails?.customImplants || [])
      setParLevel(trayDetails.parLevel || 1)
      setInitialTrayScrews(trayDetails.screws || [])
      setIsQuantityExceededModalOpen(false)
      setQuantityValidationData(null)
    }
  }, [
    pathname,
    loading,
    subTrayItemId,
    error,
    subTrayItem,
    trayDetails,
    setSpdTrayScrews,
    setSpdTrayPlates,
    setOtherItems,
    setCustomImplants,
    setParLevel,
  ])

  useEffect(() => {
    if (trayDetails?.trayImageHistory?.records) {
      const sortedRecords = [...trayDetails.trayImageHistory.records].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
      setImageHistory(sortedRecords)
    } else if (trayDetails?.trayImg) {
      setImageHistory([
        {
          imageUrl: trayDetails.trayImg,
          addedBy: { email: 'Unknown', role: 'spd' },
          timestamp: new Date(),
        },
      ])
    } else {
      setImageHistory([])
    }
    setCurrentImageIndex(0)
    setIsViewingHistory(false)
  }, [trayDetails])

  const handleEditModeChange = () => {
    setIsInEditMode((prevMode) => !prevMode)
  }

  const handleViewDetailsClick = () => {
    navigate(`../${trayId}/${subTrayItemId}`)
  }

  const handleCaptureReferenceImage = () => {
    navigate(`/tray-management/${trayId}/${subTrayItemId}/capture`)
  }

  const handleConfirmationClick = (isRejecting?: boolean) => {
    if (isRejecting) {
      setIsRejecting(isRejecting)
    }

    setIsReferenceImageAccurateModalOpen(true)
  }

  const handleReferenceImageAccurate = () => {
    setIsReferenceImageAccurateModalOpen(false)
    setIsConfirmationOpen(true)
  }

  const handleCaptureNewReferenceImage = () => {
    setIsReferenceImageAccurateModalOpen(false)
    handleCaptureReferenceImage()
  }

  const handleLoadWithUDIsClick = () => {
    setIsLoadingWithUDIs(true)
    setIsUDIScannerModalOpen(true)
  }

  const handleLoadWithDIClick = () => {
    setIsLoadingWithUDIs(false)
    setIsUDIScannerModalOpen(false)
    setScannedUDI('')
  }

  const handleUDIScan = (scannedCode: string) => {
    setScannedUDI(scannedCode)
  }

  const handleUDIScannerModalClose = () => {
    setIsUDIScannerModalOpen(false)
    setScannedUDI('')
  }

  const handleTrayStateUpdate = (response: any) => {
    const analysisResults =
      response.analysis_results ||
      response.processImageData?.tray_analysis ||
      response.processImageData?.result?.tray_analysis ||
      response.processImageData?.result?.analysis_results ||
      []
    const scannedProduct = response.scannedProduct
    const continuousScannedProducts = response.continuousScannedProducts
    const scannedQuantity = response.quantity || 1

    if (response.processedImageUrl) {
      const newImageRecord = {
        imageUrl: response.processedImageUrl,
        addedBy: { email: 'UDI Scanner', role: 'spd' as const },
        timestamp: new Date(),
      }

      setImageHistory((prev) => [newImageRecord, ...prev])
      setCurrentImageIndex(0)
      setIsViewingHistory(false)
    }

    // We may need to conditionally show the "no new screws" info if plates were not increased
    let pendingNoNewScrewsInfo = false
    let increasedPlates = false

    if (analysisResults && Array.isArray(analysisResults)) {
      const removeDuplicateScrews = (screws: any[]) => {
        const uniqueObjectsMap = new Map<string, any>()
        for (const obj of screws) {
          const key = `${obj.column_index}_${obj.row_index}_${obj.section_label}`
          uniqueObjectsMap.set(key, obj)
        }
        return Array.from(uniqueObjectsMap.values())
      }

      const screwsFromAnalysis = removeDuplicateScrews(analysisResults)
      const detectedPositions = screwsFromAnalysis.filter(
        (screw: any) => screw.removed === false
      )

      let allDetectedScrews: any[] = []

      if (continuousScannedProducts && continuousScannedProducts.length > 0) {
        // For continuous scanning, distribute all detected positions across the scanned products (excluding error products)
        const validProducts = continuousScannedProducts.filter(
          (p: any) => !p.error
        )
        allDetectedScrews = detectedPositions.map(
          (screw: any, index: number) => {
            const productIndex = index % validProducts.length
            const product = validProducts[productIndex]

            return {
              label: screw.section_label,
              row: screw.row_index,
              column: screw.column_label,
              x: screw.column_index,
              deviceId:
                product.deviceId ||
                `DETECTED_${Date.now()}_${screw.row_index}_${
                  screw.column_index
                }`,
              deviceDescription: product.productName || 'Detected Item',
              company: {
                name: product.manufacturer || 'Unknown',
                id: 0,
                regEmail: '',
              },
              expirationDate: product.expirationDate || null,
              lotBatch: product.lotNumber || null,
              screwSize:
                getScrewSizeFromProduct(product.deviceId, product.deviceId) ||
                null,
              wasted: false,
              loadedViaUDI: true,
              loadedAt: new Date(),
            }
          }
        )
      } else {
        // Single product scanning
        allDetectedScrews = detectedPositions.map((screw: any) => ({
          label: screw.section_label,
          row: screw.row_index,
          column: screw.column_label,
          x: screw.column_index,
          deviceId:
            scannedProduct?.deviceId ||
            `DETECTED_${Date.now()}_${screw.row_index}_${screw.column_index}`,
          deviceDescription: scannedProduct?.productName || 'Detected Item',
          company: {
            name: scannedProduct?.manufacturer || 'Unknown',
            id: 0,
            regEmail: '',
          },
          expirationDate: scannedProduct?.expirationDate || null,
          lotBatch: scannedProduct?.lotNumber || null,
          screwSize:
            getScrewSizeFromProduct(
              scannedProduct?.deviceId,
              scannedProduct?.deviceId
            ) || null,
          wasted: false,
          loadedViaUDI: !!scannedProduct,
          loadedAt: new Date(),
        }))
      }

      const trulyNewScrews = allDetectedScrews.filter((detectedScrew) => {
        const positionKey = `${detectedScrew.label}-${detectedScrew.row}-${detectedScrew.x}`
        const wasInitiallyOccupied = initialTrayScrews.some(
          (initialScrew) =>
            `${initialScrew.label}-${initialScrew.row}-${initialScrew.x}` ===
            positionKey
        )
        return !wasInitiallyOccupied
      })

      if (
        (scannedProduct ||
          (continuousScannedProducts &&
            continuousScannedProducts.filter((p: any) => !p.error).length >
              0)) &&
        trulyNewScrews.length > scannedQuantity
      ) {
        const validationData = {
          scannedQuantity: scannedQuantity,
          detectedQuantity: trulyNewScrews.length,
          productName:
            continuousScannedProducts &&
            continuousScannedProducts.filter((p: any) => !p.error).length > 0
              ? `${
                  continuousScannedProducts.filter((p: any) => !p.error).length
                } different products`
              : scannedProduct?.productName || 'Unknown Product',
        }

        setQuantityValidationData(validationData)
        setIsQuantityExceededModalOpen(true)
        return
      }

      if (trulyNewScrews.length > 0) {
        setSpdTrayScrews((prev) => [...prev, ...trulyNewScrews])
        setInitialTrayScrews((prev) => [...prev, ...trulyNewScrews])

        const message =
          continuousScannedProducts && continuousScannedProducts.length > 0
            ? `Successfully loaded ${trulyNewScrews.length} new screws from ${
                continuousScannedProducts.filter((p: any) => !p.error).length
              } different products to the tray map!`
            : scannedProduct
            ? `Successfully loaded ${trulyNewScrews.length} new screws to the tray map!`
            : `Successfully detected and mapped ${trulyNewScrews.length} new items to the tray!`
        enqueueSnackbar(message, { variant: 'success' })
      } else {
        // Defer this info message until after plate handling so we don't mislead during plate-only loading
        pendingNoNewScrewsInfo = true
      }
    } else if (response.error) {
      enqueueSnackbar(`Failed to process tray image: ${response.error}`, {
        variant: 'error',
      })
    } else {
      enqueueSnackbar('Invalid response format from tray processing', {
        variant: 'warning',
      })
    }

    try {
      const plateRegions =
        response?.processImageData?.result?.regions?.filter(
          (region: any) => region.type === 'plate'
        ) || []

      if (plateRegions.length > 0) {
        const cvAreas = plateRegions[0].areas || []
        const previousPlates = (trayPlates || []).slice()
        let anyIncrease = false
        let totalIncrease = 0

        const merged: any[] = []

        // Build a lookup for previous plates by name
        const prevByName = new Map<string, any>()
        previousPlates.forEach((p: any) => prevByName.set(p.plateName, p))

        // Deduplicate CV areas by plate label and use the maximum observed count
        const labelToCount = new Map<string, number>()
        cvAreas.forEach((area: any) => {
          const label = area?.label
          if (!label) return
          const count = Number(area?.count || 0)
          const prevMax = labelToCount.get(label)
          labelToCount.set(label, Math.max(prevMax ?? 0, count))
        })

        labelToCount.forEach((newCount, label) => {
          const prev = prevByName.get(label)
          if (prev) {
            const increased = newCount > (prev.plateCount || 0)
            if (increased) anyIncrease = true
            if (increased) totalIncrease += newCount - (prev.plateCount || 0)
            merged.push({
              ...prev,
              plateCount: newCount,
              loadedViaUDI: increased,
              loadedAt: increased ? new Date() : prev.loadedAt,
            })
          } else {
            const existingDetails = (trayDetails?.plates || []).find(
              (p) => p.plateName === label
            )
            const increased = newCount > 0
            if (increased) anyIncrease = true
            if (increased) totalIncrease += newCount
            merged.push({
              deviceId: existingDetails?.deviceId,
              deviceDescription: existingDetails?.deviceDescription,
              plateName: label,
              plateCount: newCount,
              loadedViaUDI: increased,
              loadedAt: increased ? new Date() : undefined,
            })
          }
        })

        previousPlates.forEach((prev: any) => {
          if (!labelToCount.has(prev.plateName)) {
            merged.push(prev)
          }
        })

        // Validate scanned quantity vs total increase in plates
        const hasScannedProducts =
          Boolean(scannedProduct) ||
          Boolean(
            continuousScannedProducts &&
              continuousScannedProducts.filter((p: any) => !p.error).length > 0
          )

        if (hasScannedProducts && totalIncrease > scannedQuantity) {
          const validationData = {
            scannedQuantity: scannedQuantity,
            detectedQuantity: totalIncrease,
            productName:
              continuousScannedProducts &&
              continuousScannedProducts.filter((p: any) => !p.error).length > 0
                ? `${
                    continuousScannedProducts.filter((p: any) => !p.error)
                      .length
                  } different products`
                : scannedProduct?.productName || 'Unknown Product',
          }

          setQuantityValidationData(validationData)
          setIsQuantityExceededModalOpen(true)
          return
        }

        setSpdTrayPlates(merged as any)
        if (anyIncrease) {
          enqueueSnackbar('Updated plate counts from image analysis', {
            variant: 'success',
          })
        }

        if (anyIncrease) {
          increasedPlates = true
        }
      }
    } catch (_) {
      // noop: best-effort plate handling
    }

    // Only show the deferred no-screws info when applicable (screw trays with no plate increase)
    if (
      pendingNoNewScrewsInfo &&
      !increasedPlates &&
      hasScrews &&
      !isPlatesOnly
    ) {
      enqueueSnackbar(
        'No new screws detected in empty positions. All detected screws may already be loaded.',
        { variant: 'info' }
      )
    }
  }

  const handleConfirmationClose = () => {
    setIsRejecting(false)
    setIsConfirmationOpen(false)
  }

  const handleUserConfirmation = async () => {
    if (!subTrayItem || !trayDetails) {
      return
    }
    setIsUpdating(true)

    try {
      let updatedTrayDetails: SubTrayItemProductDetails = {
        ...trayDetails,
        screws: trayScrews,
        plates: trayPlates,
        otherItems,
        customImplants,
      }

      const newUDIImages = imageHistory.filter(
        (img) =>
          img.addedBy.email === 'UDI Scanner' &&
          !trayDetails.trayImageHistory?.records.some(
            (existing) => existing.imageUrl === img.imageUrl
          )
      )

      for (const newImage of newUDIImages) {
        updatedTrayDetails = updateTrayImageHistory(
          updatedTrayDetails,
          newImage.imageUrl,
          'UDI Scanner',
          'spd'
        )
      }

      const editResult = await editSubTrayItem({
        id: subTrayItem.id,
        productDetails: JSON.stringify(updatedTrayDetails),
      })

      if (editResult) {
        setTrayDetails((prev) => (prev ? updatedTrayDetails : undefined))
        setIsInEditMode(false) // Exit edit mode after successful confirmation
        enqueueSnackbar('Sub tray updated successfully', { variant: 'success' })
      }
    } catch (error) {
      enqueueSnackbar('Failed to update sub tray', { variant: 'error' })
    } finally {
      setIsUpdating(false)
    }

    setIsConfirmationOpen(false)
  }

  const handleQuantityExceededConfirm = () => {
    setIsQuantityExceededModalOpen(false)
    setQuantityValidationData(null)
  }

  const handleQuantityExceededCancel = () => {
    setIsQuantityExceededModalOpen(false)
    setQuantityValidationData(null)
  }

  const renderSubTrayPlates = () => (
    <>
      {existingPlates.map((trayPlate) => (
        <div key={trayPlate.plateName} className="sub-tray-plate_container">
          <QrCode2 className="qr-code" />
          <div className="content">
            <Typography variant="subtitle2">
              Plate Name: {trayPlate?.plateName}
            </Typography>
            <Typography variant="subtitle2">
              Device ID: {trayPlate.deviceId}
            </Typography>
            {trayPlate.deviceDescription && (
              <Typography variant="subtitle2">
                Device Description: {trayPlate.deviceDescription}
              </Typography>
            )}
            <Typography variant="subtitle2">
              Count: {trayPlate.plateCount}
            </Typography>
          </div>
        </div>
      ))}
    </>
  )

  const renderNewlyLoadedScrews = () => (
    <>
      {newlyLoadedScrews.map((screw, index) => (
        <div
          key={`${screw.deviceId}_${index}`}
          className="sub-tray-plate_container newly-loaded-screw"
        >
          <QrCode2 className="qr-code" style={{ color: '#2196F3' }} />
          <div className="content">
            <Typography variant="subtitle2">
              Position: {screw.label}
              {screw.row}-{screw.column}
            </Typography>
            <Typography variant="subtitle2">
              Device ID: {screw.deviceId}
            </Typography>
            {screw.screwSize && (
              <Typography variant="subtitle2">
                Screw Size: {screw.screwSize}
              </Typography>
            )}
            {screw.lotBatch && (
              <Typography variant="subtitle2">
                Lot/Batch: {screw.lotBatch}
              </Typography>
            )}
            <Typography variant="subtitle2">
              Device Description: {screw.deviceDescription}
            </Typography>
            <Typography variant="subtitle2">
              Manufacturer: {screw.company?.name || 'Unknown'}
            </Typography>
            {screw.expirationDate && (
              <Typography variant="subtitle2">
                Expires: {screw.expirationDate}
              </Typography>
            )}
            <Typography
              variant="subtitle2"
              style={{ color: '#2196F3', fontWeight: 'bold' }}
            >
              Loaded via UDI:{' '}
              {dayjs(screw.loadedAt).format('MMM D, YYYY h:mm A')}
            </Typography>
          </div>
        </div>
      ))}
    </>
  )

  const handleAddCustomImplantTrigger = () => {
    setIsCustomImplantModalOpen((prev) => !prev)
  }

  const {
    data: searchProductResult,
    loading: isSearchingProduct,
    error: searchProductError,
  } = useSearchProduct({
    udi: scannedUDI,
    enabled: Boolean(scannedUDI),
  })

  return {
    isInEditMode,
    setIsInEditMode,
    handleEditModeChange,
    loading,
    error,
    subTrayItem,
    trayDetails,
    handleViewDetailsClick,
    isConfirmationOpen,
    setIsConfirmationOpen,
    handleConfirmationClick,
    handleLoadWithUDIsClick,
    handleLoadWithDIClick,
    isLoadingWithUDIs,
    isAdditionalDeviceCountModalOpen,
    isRejecting,
    handleConfirmationClose,
    handleUserConfirmation,
    isUpdating,
    renderSubTrayPlates,
    renderNewlyLoadedScrews,
    newlyLoadedScrews,
    trayScrews,
    trayPlates,
    isLoadMultipleBoxVisible,
    isInstrumentTray,
    hasPlates,
    loadAllScrews,
    increaseAllPlatesBy,
    isAllScrewsLoaded,
    isAllPlatesLoaded,
    hasScrews,
    isPlatesOnly,
    totalPlateCount,
    existingPlates,
    totalScrews,
    imageScale,
    setImageScale,
    imageRotation,
    setImageRotation,
    isLegendOpen,
    setIsLegendOpen,
    currentImageIndex,
    imageHistory,
    isViewingHistory,
    hasMultipleImages,
    goToPreviousImage,
    goToNextImage,
    goToLatestImage,
    trayImage,
    isImageLoading,
    formattedTimestamp,
    referenceImageTitle,
    isImagePreviewOpen,
    setIsImagePreviewOpen,
    handleCaptureReferenceImage,
    isReferenceImageAccurateModalOpen,
    setIsReferenceImageAccurateModalOpen,
    handleReferenceImageAccurate,
    handleCaptureNewReferenceImage,
    otherItems,
    setOtherItems,
    isCustomImplantTray,
    isCustomImplantModalOpen,
    handleAddCustomImplantTrigger,
    handleUDIScan,
    handleTrayStateUpdate,
    isUDIScannerModalOpen,
    setIsUDIScannerModalOpen,
    scannedUDI,
    setScannedUDI,
    searchProductResult,
    isSearchingProduct,
    searchProductError,
    handleUDIScannerModalClose,
    isQuantityExceededModalOpen,
    setIsQuantityExceededModalOpen,
    quantityValidationData,
    handleQuantityExceededConfirm,
    handleQuantityExceededCancel,
  }
}

export default useEditTray
